// src/ui/widgets/DesktopNavRail.jsx
//
// Desktop-only vertical nav rail — the left-hand icon strip from the
// Magnific screenshot, standing in for BottomNavBar on large screens.
// Hidden below the `lg` breakpoint via `hidden lg:flex`, so it never
// renders (and never affects layout) on mobile.
//
// The "+" button opens the same Status / Want / Listing pop-out as the
// mobile PostAddFab, anchored to the right of the rail.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Home, Radio, Search, X } from 'lucide-react';
import { UserAvatar } from '../../components/UserAvatar';
import { userProfileService } from '../../services/user/userProfileService';
import { PostAddOption } from '../screens/post/widgets/PostAddFab';

import chatIcon from '../../assets/svg/chat_icon.svg';
import chatFilled from '../../assets/svg/chat_filled.svg';
import homeIcon from '../../assets/svg/home_icon.svg';
import homeFilled from '../../assets/svg/home_filled.svg';
import filatomLogo from '../../assets/images/filatom_icon.jpeg';

const WHITE = '#FFFFFF';
const GREY = '#7A7A7A';
const ORANGE = '#FF8C08';
const BLUE = '#4DA3FF';
const CARD = '#23262C';

const RAIL_WIDTH = 76;
const RAIL_GAP = 18;
const ADD_BTN_SIZE = 44;

// Same three options (and order) as the mobile PostAddFab.
const ADD_OPTIONS = [
  {
    value: PostAddOption.STATUS,
    icon: Radio,
    label: 'Status',
    subtitle: 'Share a quick text or photo update',
  },
  {
    value: PostAddOption.WANT,
    icon: Search,
    label: 'Want',
    subtitle: "Post what you're looking for",
  },
  {
    value: PostAddOption.LISTING,
    icon: Home,
    label: 'Listing',
    subtitle: 'Post a property, service or item',
  },
];

function RailSvgIcon({ src, size = 22, color }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        backgroundColor: color,
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        pointerEvents: 'none',
      }}
    />
  );
}

function RailButton({ onClick, isActive, label, children, unreadCount = 0 }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      style={{
        width: 48,
        height: 48,
        borderRadius: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isActive ? 'rgba(255,255,255,0.10)' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background-color 150ms ease',
      }}
      onMouseEnter={(e) => {
        if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {children}
      {unreadCount > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 2,
            right: 2,
            minWidth: 15,
            height: 15,
            padding: '0 3px',
            borderRadius: 8,
            backgroundColor: BLUE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span style={{ color: '#fff', fontSize: 9, fontWeight: 700 }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        </div>
      )}
    </button>
  );
}

// ── Pop-out menu (Status / Want / Listing) ───────────────────────────────
//
// Rendered through a portal so the blurred backdrop covers the whole page
// (the rail itself is a fixed element with its own stacking context).
// `anchor` is the "+" button's on-screen rect, so the options and the
// "X" close button line up with it exactly.

function AddMenu({ anchor, onSelect, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const optionsLeft = RAIL_WIDTH + 16;

  return createPortal(
    <>
      <style>{MENU_CSS}</style>
      <div className="dnr-overlay">
        <div className="dnr-backdrop" onClick={onClose} />

        {/* Options — stacked to the right of the rail, starting at the
            "+" button's top edge. */}
        <div
          className="dnr-options"
          style={{ left: optionsLeft, top: anchor.top }}
        >
          {ADD_OPTIONS.map((opt, i) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                type="button"
                className="dnr-pill"
                style={{ animationDelay: `${i * 60}ms` }}
                onClick={() => onSelect(opt.value)}
              >
                <div className="dnr-pill-icon">
                  <Icon size={19} color={ORANGE} />
                </div>
                <div className="dnr-pill-text">
                  <span className="dnr-pill-label">{opt.label}</span>
                  <span className="dnr-pill-subtitle">{opt.subtitle}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* "+" morphs into "X" in the exact same spot */}
        <button
          type="button"
          className="dnr-close"
          style={{
            left: anchor.left,
            top: anchor.top,
            width: ADD_BTN_SIZE,
            height: ADD_BTN_SIZE,
          }}
          onClick={onClose}
          aria-label="Close"
          title="Close"
        >
          <X size={22} color="#000" />
        </button>
      </div>
    </>,
    document.body
  );
}

export const DesktopNavRail = ({ currentIndex, onTap, totalUnreadCount = 0 }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null); // null = closed
  const addBtnRef = useRef(null);

  useEffect(() => {
    const loadProfile = async () => {
      const cached = userProfileService.cachedProfile;
      if (cached) {
        setProfile(cached);
        return;
      }
      const profileData = await userProfileService.getCurrentUserProfile();
      if (profileData) setProfile(profileData);
    };
    loadProfile();
  }, []);

  const handleTap = (index) => {
    if (onTap) onTap(index);
  };

  const openMenu = () => {
    const rect = addBtnRef.current?.getBoundingClientRect();
    setMenuAnchor(
      rect
        ? { top: rect.top, left: rect.left }
        : { top: 70, left: (RAIL_WIDTH - ADD_BTN_SIZE) / 2 }
    );
  };

  const closeMenu = useCallback(() => setMenuAnchor(null), []);

  // Same routing as PostScreen.handlePostAddSelect.
  const handleSelect = useCallback(
    (value) => {
      setMenuAnchor(null);
      // Let the close transition read before navigating away.
      setTimeout(() => {
        if (value === PostAddOption.LISTING) navigate('/post/add-property');
        else if (value === PostAddOption.WANT) navigate('/post/add-want');
        else if (value === PostAddOption.STATUS) navigate('/post/add-status');
      }, 150);
    },
    [navigate]
  );

  return (
    <div
      className="hidden lg:flex"
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        width: RAIL_WIDTH,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '18px 0 22px',
        backgroundColor: '#0D0E11',
        borderRight: '1px solid #1C1E23',
        zIndex: 60,
      }}
    >
      {/* Single evenly-spaced column: logo, add button, home, chats, profile */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: RAIL_GAP }}>
        {/* Brand mark / logo */}
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 11,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={filatomLogo}
            alt="Filatom"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 11,
            }}
          />
        </div>

        {/* Add button — opens the Status / Want / Listing pop-out */}
        <button
          ref={addBtnRef}
          type="button"
          onClick={openMenu}
          title="Add"
          aria-label="Add"
          style={{
            width: ADD_BTN_SIZE,
            height: ADD_BTN_SIZE,
            borderRadius: 14,
            backgroundColor: 'rgba(255,140,8,0.12)',
            border: `1px solid ${ORANGE}40`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="2.2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>

        {/* Home */}
        <RailButton onClick={() => handleTap(1)} isActive={currentIndex === 1} label="Home">
          <RailSvgIcon
            src={currentIndex === 1 ? homeFilled : homeIcon}
            size={26}
            color={currentIndex === 1 ? WHITE : GREY}
          />
        </RailButton>

        {/* Chats */}
        <RailButton
          onClick={() => handleTap(0)}
          isActive={currentIndex === 0}
          label="Chats"
          unreadCount={totalUnreadCount}
        >
          <RailSvgIcon
            src={currentIndex === 0 ? chatFilled : chatIcon}
            size={22}
            color={currentIndex === 0 ? WHITE : GREY}
          />
        </RailButton>

        {/* Profile */}
        <button
          type="button"
          onClick={() => handleTap(2)}
          title="Your profile"
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: currentIndex === 2 ? `1.8px solid ${WHITE}` : '1.8px solid transparent',
            cursor: 'pointer',
          }}
        >
          <UserAvatar
            size={28}
            imageUrl={profile?.hasProfileImage ? profile.profileImage : undefined}
            name={profile?.displayName}
          />
        </button>
      </div>

      {menuAnchor && (
        <AddMenu anchor={menuAnchor} onSelect={handleSelect} onClose={closeMenu} />
      )}
    </div>
  );
};

const MENU_CSS = `
.dnr-overlay{position:fixed;inset:0;z-index:200;}
.dnr-backdrop{position:absolute;inset:0;background:rgba(0,0,0,.55);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);animation:dnrFade 260ms ease-out;}
@keyframes dnrFade{from{opacity:0;}to{opacity:1;}}

.dnr-options{position:fixed;display:flex;flex-direction:column;align-items:flex-start;gap:14px;z-index:201;}
.dnr-pill{display:flex;align-items:center;gap:12px;padding:12px 18px 12px 12px;background:${CARD};border:1px solid rgba(255,140,8,.30);border-radius:28px;box-shadow:0 4px 14px rgba(0,0,0,.30);cursor:pointer;animation:dnrPop 320ms cubic-bezier(.2,.9,.25,1.3) both;transition:border-color 150ms ease,background-color 150ms ease;}
.dnr-pill:hover{border-color:rgba(255,140,8,.6);background:#2C2F36;}
@keyframes dnrPop{from{opacity:0;transform:scale(.85) translateX(-8px);}to{opacity:1;transform:scale(1) translateX(0);}}
.dnr-pill-text{display:flex;flex-direction:column;align-items:flex-start;text-align:left;}
.dnr-pill-label{color:#fff;font-size:14px;font-weight:700;}
.dnr-pill-subtitle{color:rgba(255,255,255,.38);font-size:11px;margin-top:1px;white-space:nowrap;}
.dnr-pill-icon{width:38px;height:38px;border-radius:50%;background:rgba(255,140,8,.15);display:flex;align-items:center;justify-content:center;flex:none;}

.dnr-close{position:fixed;border-radius:14px;background:${ORANGE};border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;z-index:202;animation:dnrRotate 320ms cubic-bezier(.2,.9,.25,1.3) both;}
@keyframes dnrRotate{from{transform:rotate(-45deg) scale(.9);opacity:.4;}to{transform:rotate(0deg) scale(1);opacity:1;}}

@media (prefers-reduced-motion:reduce){
  .dnr-pill,.dnr-close,.dnr-backdrop{animation:none;}
}
`;

export default DesktopNavRail;